// File: src/pages/Event/EventReportViewer.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/axios";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  Eye,
  RefreshCw,
  Loader2,
  FileText,
  FileSpreadsheet,
  AlertTriangle,
  CheckCircle2,
  Sparkles,
  BarChart3,
  Wallet,
  Users,
  UtensilsCrossed,
  ClipboardList,
  Activity,
  Briefcase,
  TrendingUp,
  PoundSterling,
  CalendarDays,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useUserPermissions } from "@/hooks/useUserPermissions";

function safeNum(v, d = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
}

function money(v) {
  return `£${safeNum(v, 0).toFixed(2)}`;
}

function pct(v) {
  return `${safeNum(v, 0).toFixed(2)}%`;
}

function safeStr(v, d = "—") {
  const s = v == null ? "" : String(v).trim();
  return s ? s : d;
}

function formatDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("en-GB", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

function formatDateTime(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString("en-GB");
}

function fileSafeName(v, fallback = "event-report") {
  const cleaned = String(v || fallback)
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
  return cleaned || fallback;
}

async function apiGetFirst(paths, config = {}) {
  let lastError = null;

  for (const path of paths) {
    try {
      const response = await api.get(path, config);
      return { response, usedPath: path };
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError || new Error("No compatible endpoint found");
}

async function downloadBlobFromFirst(urls, filename, mimeType) {
  const { response, usedPath } = await apiGetFirst(urls, { responseType: "blob" });
  const blob = new Blob([response.data], { type: mimeType });
  const objectUrl = window.URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();

  window.URL.revokeObjectURL(objectUrl);

  return usedPath;
}

const REPORT_PACK_ENDPOINTS = (eventId) => [
  `/solo-mdsh-api/event/${eventId}/report-pack`,
  `/solo-mdsh-api/events/${eventId}/report-pack`,
];

const REPORT_PDF_ENDPOINTS = (eventId) => [
  `/solo-mdsh-api/event/${eventId}/report/pdf`,
  `/solo-mdsh-api/event/${eventId}/pdf`,
  `/solo-mdsh-api/events/${eventId}/report/pdf`,
  `/solo-mdsh-api/events/${eventId}/pdf`,
];

const REPORT_XLSX_ENDPOINTS = (eventId) => [
  `/solo-mdsh-api/event/${eventId}/report/xlsx`,
  `/solo-mdsh-api/event/${eventId}/report/excel`,
  `/solo-mdsh-api/event/${eventId}/xlsx`,
  `/solo-mdsh-api/event/${eventId}/excel`,
  `/solo-mdsh-api/events/${eventId}/report/xlsx`,
  `/solo-mdsh-api/events/${eventId}/report/excel`,
  `/solo-mdsh-api/events/${eventId}/xlsx`,
  `/solo-mdsh-api/events/${eventId}/excel`,
];

function MetricCard({ title, value, subtitle, valueClassName = "text-white", icon: Icon = Activity }) {
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/80 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500">{title}</div>
          <div className={`mt-2 text-2xl font-semibold ${valueClassName}`}>{value}</div>
          {subtitle ? <div className="mt-1 text-xs text-slate-400">{subtitle}</div> : null}
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-950 p-2">
          <Icon className="h-4 w-4 text-cyan-300" />
        </div>
      </div>
    </div>
  );
}

function DataPill({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/70 px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-sm font-medium text-slate-100">{value}</div>
    </div>
  );
}

function InsightCard({ item, index }) {
  const severity = String(item?.severity || "").toLowerCase();

  const tone =
    severity === "high"
      ? "border-rose-500/20 bg-rose-500/5"
      : severity === "medium"
        ? "border-amber-500/20 bg-amber-500/5"
        : "border-cyan-500/20 bg-cyan-500/5";

  const badgeTone =
    severity === "high"
      ? "border-rose-500/30 bg-rose-500/10 text-rose-300"
      : severity === "medium"
        ? "border-amber-500/30 bg-amber-500/10 text-amber-300"
        : "border-cyan-500/30 bg-cyan-500/10 text-cyan-300";

  return (
    <div className={`rounded-2xl border p-4 ${tone}`}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Badge className={badgeTone}>{safeStr(item?.severity, "info")}</Badge>
        <Badge className="border-slate-700 bg-slate-900 text-slate-300">#{index + 1}</Badge>
        <span className="text-xs text-slate-500">{safeStr(item?.code, "NO_CODE")}</span>
      </div>

      <h3 className="text-base font-semibold text-white">{safeStr(item?.title)}</h3>
      <p className="mt-2 text-sm leading-6 text-slate-300">{safeStr(item?.detail)}</p>
    </div>
  );
}

function SuggestionCard({ item, index }) {
  const priority = String(item?.priority || "").toLowerCase();

  const tone =
    priority === "high"
      ? "border-rose-500/20 bg-rose-500/5"
      : priority === "medium"
        ? "border-amber-500/20 bg-amber-500/5"
        : "border-cyan-500/20 bg-cyan-500/5";

  const badgeTone =
    priority === "high"
      ? "border-rose-500/30 bg-rose-500/10 text-rose-300"
      : priority === "medium"
        ? "border-amber-500/30 bg-amber-500/10 text-amber-300"
        : "border-cyan-500/30 bg-cyan-500/10 text-cyan-300";

  return (
    <div className={`rounded-2xl border p-4 ${tone}`}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Badge className={badgeTone}>{safeStr(item?.priority, "info")}</Badge>
        <Badge className="border-slate-700 bg-slate-900 text-slate-300">#{index + 1}</Badge>
        <span className="text-xs uppercase tracking-wide text-slate-500">{safeStr(item?.area)}</span>
      </div>

      <h3 className="text-base font-semibold text-white">{safeStr(item?.title)}</h3>
      <p className="mt-2 text-sm leading-6 text-slate-300">{safeStr(item?.detail)}</p>
    </div>
  );
}

function EmptyState({ text = "No records available." }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-800 bg-slate-900/40 p-6 text-center text-sm text-slate-400">
      {text}
    </div>
  );
}

function TableWrap({ children }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-950/50">
      {children}
    </div>
  );
}

export default function EventReportViewer() {
  const { eventId } = useParams();
  const navigate = useNavigate();

  const {
    canViewEventReports,
    canViewEventBilling,
    canViewEventStaffing,
    canViewEventMenu,
  } = useUserPermissions();

  const [reportPack, setReportPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState("");

  const loadReport = useCallback(async () => {
    if (!eventId) return;

    try {
      setLoading(true);
      const { response } = await apiGetFirst(REPORT_PACK_ENDPOINTS(eventId));
      setReportPack(response.data || null);
    } catch (err) {
      console.error("Failed to load event report pack:", err);
      toast.error(err?.response?.data?.error || "Failed to load event report");
      setReportPack(null);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (!canViewEventReports) {
      setLoading(false);
      return;
    }
    loadReport();
  }, [loadReport, canViewEventReports]);

  const eventInfo = reportPack?.event || {};
  const tenant = reportPack?.tenant || {};
  const summary = reportPack?.management_summary || {};
  const analysis = reportPack?.analysis || {};
  const commercial = reportPack?.commercial || {};
  const forecast = reportPack?.forecast || {};
  const actualRevenue = reportPack?.actual_revenue || {};
  const forecastVsActual = reportPack?.forecast_vs_actual || {};
  const menuItems = Array.isArray(reportPack?.menuItems) ? reportPack.menuItems : [];
  const smartInsights = Array.isArray(reportPack?.smart_insights) ? reportPack.smart_insights : [];
  const suggestions = Array.isArray(analysis?.suggestions) ? analysis.suggestions : [];
  const riskFlags = Array.isArray(analysis?.risk_flags) ? analysis.risk_flags : [];
  const missedOpportunities = Array.isArray(analysis?.missed_opportunities) ? analysis.missed_opportunities : [];
  const commercialTx = Array.isArray(commercial?.transactions) ? commercial.transactions : [];
  const staffingVarianceRows = Array.isArray(forecastVsActual?.staffing_by_cost_centre)
    ? forecastVsActual.staffing_by_cost_centre
    : [];
  const financialVarianceRows = Array.isArray(forecastVsActual?.financial_summary)
    ? forecastVsActual.financial_summary
    : [];

  const pdfNameBase = useMemo(() => {
    return fileSafeName(eventInfo?.event_name, `event-${eventId}`);
  }, [eventInfo?.event_name, eventId]);

  const handleDownload = async (type) => {
    if (!eventId) return;

    const key = `${type}-${eventId}`;

    try {
      setBusyKey(key);

      if (type === "pdf") {
        const usedPath = await downloadBlobFromFirst(
          REPORT_PDF_ENDPOINTS(eventId),
          `${pdfNameBase}-management-pack.pdf`,
          "application/pdf"
        );
        toast.success(`PDF downloaded (${usedPath})`);
      } else {
        const usedPath = await downloadBlobFromFirst(
          REPORT_XLSX_ENDPOINTS(eventId),
          `${pdfNameBase}-management-pack.xlsx`,
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        );
        toast.success(`Excel downloaded (${usedPath})`);
      }
    } catch (err) {
      console.error(`Failed to download ${type}:`, err);
      toast.error(err?.response?.data?.error || `Failed to download ${type.toUpperCase()}`);
    } finally {
      setBusyKey("");
    }
  };

  if (!canViewEventReports) {
    return (
      <div className="min-h-screen bg-slate-950 px-4 py-6 text-slate-100 md:px-6 xl:px-8">
        <div className="mx-auto max-w-7xl space-y-4">
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            className="border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>

          <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-8 text-center">
            <AlertTriangle className="mx-auto mb-3 h-7 w-7 text-amber-300" />
            <div className="text-lg font-semibold text-white">Access restricted</div>
            <p className="mt-2 text-sm text-slate-400">
              You do not currently have permission to view event reports.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 px-4 py-6 text-slate-100 md:px-6 xl:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-8 text-center text-slate-300">
            <Loader2 className="mx-auto mb-3 h-6 w-6 animate-spin text-cyan-400" />
            Loading event report viewer...
          </div>
        </div>
      </div>
    );
  }

  if (!reportPack) {
    return (
      <div className="min-h-screen bg-slate-950 px-4 py-6 text-slate-100 md:px-6 xl:px-8">
        <div className="mx-auto max-w-7xl space-y-4">
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            className="border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>

          <div className="rounded-2xl border border-rose-500/20 bg-rose-500/5 p-8 text-center">
            <AlertTriangle className="mx-auto mb-3 h-7 w-7 text-rose-300" />
            <div className="text-lg font-semibold text-white">Report not available</div>
            <p className="mt-2 text-sm text-slate-400">
              The event report pack could not be loaded for this event.
            </p>
            <div className="mt-4">
              <Button onClick={loadReport} className="bg-cyan-600 text-white hover:bg-cyan-500">
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-6 text-slate-100 md:px-6 xl:px-8">
      <div className="mx-auto flex max-w-7xl flex-col gap-6">
        <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6 shadow-2xl">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
            <div>
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <Badge className="border-cyan-500/30 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/10">
                  Live Report Viewer
                </Badge>
                <Badge className="border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-300 hover:bg-fuchsia-500/10">
                  Management Pack
                </Badge>
              </div>

              <h1 className="flex items-center gap-3 text-2xl font-bold text-white md:text-3xl">
                <Eye className="h-8 w-8 text-cyan-400" />
                {safeStr(eventInfo?.event_name, `Event ${eventId}`)}
              </h1>

              <p className="mt-2 max-w-3xl text-sm text-slate-400 md:text-base">
                In-app management-grade event reporting with structured analysis, insights,
                commercial detail, staffing variance, and menu margin review.
              </p>

              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <DataPill label="Event Date" value={safeStr(eventInfo?.report_event_date || eventInfo?.event_date)} />
                <DataPill label="Venue" value={safeStr(eventInfo?.venue_name)} />
                <DataPill label="Planner" value={safeStr(eventInfo?.planner_name)} />
                <DataPill label="Generated At" value={formatDateTime(reportPack?.generated_at)} />
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => navigate(-1)}
                variant="outline"
                className="border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>

              <Button
                onClick={loadReport}
                variant="outline"
                className="border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh
              </Button>

              <Button
                onClick={() => handleDownload("pdf")}
                disabled={busyKey === `pdf-${eventId}`}
                variant="outline"
                className="border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800"
              >
                {busyKey === `pdf-${eventId}` ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <FileText className="mr-2 h-4 w-4" />
                )}
                PDF
              </Button>

              <Button
                onClick={() => handleDownload("excel")}
                disabled={busyKey === `excel-${eventId}`}
                className="bg-cyan-600 text-white hover:bg-cyan-500"
              >
                {busyKey === `excel-${eventId}` ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <FileSpreadsheet className="mr-2 h-4 w-4" />
                )}
                Excel
              </Button>
            </div>
          </div>
        </div>

        <section className="grid grid-cols-1 gap-4 xl:grid-cols-4">
          <MetricCard
            title="Quote Total"
            value={money(summary?.quote_total)}
            subtitle="Commercial quoted value"
            valueClassName="text-emerald-300"
            icon={Wallet}
          />
          <MetricCard
            title="Actual Revenue"
            value={summary?.actual_total_revenue == null ? "—" : money(summary?.actual_total_revenue)}
            subtitle="Latest actual revenue"
            valueClassName="text-cyan-300"
            icon={PoundSterling}
          />
          <MetricCard
            title="Gross Margin"
            value={pct(summary?.gross_margin_pct)}
            subtitle="Before staffing"
            valueClassName="text-violet-300"
            icon={TrendingUp}
          />
          <MetricCard
            title="Readiness"
            value={`${safeNum(analysis?.readiness?.score, 0).toFixed(0)} / 100`}
            subtitle={safeStr(analysis?.readiness?.status)}
            valueClassName="text-white"
            icon={CheckCircle2}
          />
        </section>

        <section className="grid grid-cols-1 gap-5 xl:grid-cols-[1.1fr_0.9fr]">
          <Card className="border-slate-800 bg-slate-950/70 text-slate-100">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <CalendarDays className="h-5 w-5 text-cyan-400" />
                Event Identity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <DataPill label="Company" value={safeStr(tenant?.company_name || eventInfo?.company_id)} />
                <DataPill
                  label="Unit"
                  value={safeStr(tenant?.unit_slug || tenant?.unit_name || eventInfo?.unit_id)}
                />
                <DataPill label="Event Date" value={formatDate(eventInfo?.report_event_date || eventInfo?.event_date)} />
                <DataPill label="Kickoff" value={safeStr(eventInfo?.kickoff_time)} />
                <DataPill label="Venue" value={safeStr(eventInfo?.venue_name)} />
                <DataPill label="Planner" value={safeStr(eventInfo?.planner_name)} />
                <DataPill label="Theme" value={safeStr(eventInfo?.event_theme)} />
                <DataPill label="Guests" value={safeNum(summary?.guests, 0)} />
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-800 bg-slate-950/70 text-slate-100">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Briefcase className="h-5 w-5 text-fuchsia-400" />
                Performance Snapshot
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-3">
                <DataPill
                  label="Performance Band"
                  value={safeStr(analysis?.performance_summary?.performance_band)}
                />
                <DataPill
                  label="Actual Revenue Band"
                  value={safeStr(analysis?.performance_summary?.actual_revenue_band)}
                />
                <DataPill
                  label="Collection Band"
                  value={safeStr(analysis?.performance_summary?.collection_band)}
                />
                <DataPill
                  label="Staffing Control Band"
                  value={safeStr(analysis?.performance_summary?.staffing_control_band)}
                />
                <DataPill
                  label="Top Opportunity"
                  value={safeStr(analysis?.performance_summary?.top_opportunity?.title)}
                />
              </div>
            </CardContent>
          </Card>
        </section>

        <Card className="border-slate-800 bg-slate-950/70 text-slate-100">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <BarChart3 className="h-5 w-5 text-cyan-400" />
              Executive Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              <MetricCard
                title="Portions"
                value={safeNum(summary?.portions, 0)}
                subtitle="Planned serving volume"
                icon={ClipboardList}
              />
              <MetricCard
                title="Balance Due"
                value={money(summary?.balance_due)}
                subtitle="Outstanding client balance"
                valueClassName="text-rose-300"
                icon={Wallet}
              />
              <MetricCard
                title="Revenue / Guest"
                value={money(summary?.revenue_per_guest)}
                subtitle="Quoted value per guest"
                valueClassName="text-cyan-300"
                icon={Users}
              />
              <MetricCard
                title="Staffing / Guest"
                value={money(summary?.staffing_per_guest)}
                subtitle="Forecast staffing per guest"
                valueClassName="text-fuchsia-300"
                icon={Users}
              />
            </div>
          </CardContent>
        </Card>

        <section className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          <Card className="border-slate-800 bg-slate-950/70 text-slate-100">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Sparkles className="h-5 w-5 text-cyan-400" />
                Smart Insights
              </CardTitle>
            </CardHeader>
            <CardContent>
              {smartInsights.length ? (
                <div className="space-y-4">
                  {smartInsights.map((item, index) => (
                    <InsightCard key={`${item.code || "insight"}-${index}`} item={item} index={index} />
                  ))}
                </div>
              ) : (
                <EmptyState text="No smart insights available." />
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-800 bg-slate-950/70 text-slate-100">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                Recommended Actions
              </CardTitle>
            </CardHeader>
            <CardContent>
              {suggestions.length ? (
                <div className="space-y-4">
                  {suggestions.map((item, index) => (
                    <SuggestionCard key={`${item.area || "suggestion"}-${index}`} item={item} index={index} />
                  ))}
                </div>
              ) : (
                <EmptyState text="No structured recommendations available." />
              )}
            </CardContent>
          </Card>
        </section>

        <section className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          <Card className="border-slate-800 bg-slate-950/70 text-slate-100">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <AlertTriangle className="h-5 w-5 text-amber-400" />
                Risk Flags
              </CardTitle>
            </CardHeader>
            <CardContent>
              {riskFlags.length ? (
                <div className="flex flex-wrap gap-2">
                  {riskFlags.map((flag) => (
                    <Badge
                      key={flag}
                      className="border-amber-500/30 bg-amber-500/10 text-amber-300 hover:bg-amber-500/10"
                    >
                      {flag}
                    </Badge>
                  ))}
                </div>
              ) : (
                <EmptyState text="No risk flags detected." />
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-800 bg-slate-950/70 text-slate-100">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Activity className="h-5 w-5 text-fuchsia-400" />
                Missed Opportunities
              </CardTitle>
            </CardHeader>
            <CardContent>
              {missedOpportunities.length ? (
                <div className="space-y-3">
                  {missedOpportunities.map((item, index) => (
                    <div
                      key={`${item.area || "opportunity"}-${index}`}
                      className="rounded-xl border border-slate-800 bg-slate-900/70 p-4"
                    >
                      <div className="text-xs uppercase tracking-wide text-slate-500">
                        {safeStr(item?.area)}
                      </div>
                      <div className="mt-2 text-sm leading-6 text-slate-300">{safeStr(item?.detail)}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState text="No missed opportunities flagged." />
              )}
            </CardContent>
          </Card>
        </section>

        <Card className="border-slate-800 bg-slate-950/70 text-slate-100">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <TrendingUp className="h-5 w-5 text-cyan-400" />
              Forecast vs Actual
            </CardTitle>
          </CardHeader>
          <CardContent>
            {financialVarianceRows.length ? (
              <TableWrap>
                <table className="min-w-full text-sm">
                  <thead className="border-b border-slate-800 bg-slate-900/90 text-slate-300">
                    <tr>
                      <th className="px-4 py-3 text-left">Metric</th>
                      <th className="px-4 py-3 text-right">Forecast</th>
                      <th className="px-4 py-3 text-right">Actual</th>
                      <th className="px-4 py-3 text-right">Variance</th>
                      <th className="px-4 py-3 text-right">Variance %</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {financialVarianceRows.map((row, index) => (
                      <tr key={`${row.label}-${index}`} className="bg-slate-950/30">
                        <td className="px-4 py-3 text-slate-200">{safeStr(row.label)}</td>
                        <td className="px-4 py-3 text-right text-slate-300">{money(row.forecast)}</td>
                        <td className="px-4 py-3 text-right text-slate-300">
                          {row.actual == null ? "—" : money(row.actual)}
                        </td>
                        <td className="px-4 py-3 text-right text-slate-300">
                          {row.variance == null ? "—" : money(row.variance)}
                        </td>
                        <td className="px-4 py-3 text-right text-slate-300">
                          {row.variance_pct == null ? "—" : pct(row.variance_pct)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableWrap>
            ) : (
              <EmptyState text="No forecast vs actual financial summary available." />
            )}
          </CardContent>
        </Card>

        {canViewEventBilling ? (
          <section className="grid grid-cols-1 gap-5 xl:grid-cols-2">
            <Card className="border-slate-800 bg-slate-950/70 text-slate-100">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Wallet className="h-5 w-5 text-emerald-400" />
                  Commercial Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <DataPill label="Quote Status" value={safeStr(commercial?.header?.quote_status)} />
                  <DataPill label="Quote Sent At" value={formatDateTime(commercial?.header?.quote_sent_at)} />
                  <DataPill label="Quote Total" value={money(commercial?.totals?.quote_total)} />
                  <DataPill label="Deposit Required" value={money(commercial?.header?.deposit_required)} />
                  <DataPill label="Deposit Received" value={money(commercial?.totals?.deposit_received)} />
                  <DataPill label="Payments Received" value={money(commercial?.totals?.amount_paid)} />
                  <DataPill label="Adjustments Net" value={money(commercial?.totals?.adjustments_net)} />
                  <DataPill label="Charges Net" value={money(commercial?.totals?.charges_net)} />
                  <DataPill label="Refunds Net" value={money(commercial?.totals?.refunds_net)} />
                  <DataPill label="Net Received" value={money(commercial?.totals?.net_received)} />
                  <DataPill label="Balance Due" value={money(commercial?.totals?.balance_due)} />
                  <DataPill label="Payment Status" value={safeStr(commercial?.totals?.payment_status)} />
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-800 bg-slate-950/70 text-slate-100">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <PoundSterling className="h-5 w-5 text-cyan-400" />
                  Forecast + Actual Revenue
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <DataPill label="Food Sales Forecast" value={money(forecast?.food_sales)} />
                  <DataPill label="Drink Sales Forecast" value={money(forecast?.drink_sales)} />
                  <DataPill label="Room Hire" value={money(forecast?.room_hire)} />
                  <DataPill label="Sales Subtotal" value={money(forecast?.sales_subtotal)} />
                  <DataPill
                    label="Actual Food Revenue"
                    value={actualRevenue?.food_revenue == null ? "—" : money(actualRevenue?.food_revenue)}
                  />
                  <DataPill
                    label="Actual Beverage Revenue"
                    value={actualRevenue?.beverage_revenue == null ? "—" : money(actualRevenue?.beverage_revenue)}
                  />
                  <DataPill
                    label="Actual Total Revenue"
                    value={actualRevenue?.total_revenue == null ? "—" : money(actualRevenue?.total_revenue)}
                  />
                  <DataPill
                    label="Variance to Quote %"
                    value={
                      actualRevenue?.variance_to_quote_total_pct == null
                        ? "—"
                        : pct(actualRevenue?.variance_to_quote_total_pct)
                    }
                  />
                </div>
              </CardContent>
            </Card>
          </section>
        ) : null}

        {canViewEventStaffing ? (
          <>
            <Card className="border-slate-800 bg-slate-950/70 text-slate-100">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Users className="h-5 w-5 text-fuchsia-400" />
                  Staffing by Cost Centre
                </CardTitle>
              </CardHeader>
              <CardContent>
                {staffingVarianceRows.length ? (
                  <TableWrap>
                    <table className="min-w-full text-sm">
                      <thead className="border-b border-slate-800 bg-slate-900/90 text-slate-300">
                        <tr>
                          <th className="px-4 py-3 text-left">Cost Centre</th>
                          <th className="px-4 py-3 text-right">Forecast</th>
                          <th className="px-4 py-3 text-right">Actual</th>
                          <th className="px-4 py-3 text-right">Variance</th>
                          <th className="px-4 py-3 text-right">Variance %</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800">
                        {staffingVarianceRows.map((row, index) => (
                          <tr key={`${row.cost_centre_id}-${index}`} className="bg-slate-950/30">
                            <td className="px-4 py-3 text-slate-200">
                              {safeStr(row.cost_centre_name || row.cost_centre_code || row.cost_centre_id)}
                            </td>
                            <td className="px-4 py-3 text-right text-slate-300">{money(row.forecast)}</td>
                            <td className="px-4 py-3 text-right text-slate-300">
                              {row.actual == null ? "—" : money(row.actual)}
                            </td>
                            <td className="px-4 py-3 text-right text-slate-300">
                              {row.variance == null ? "—" : money(row.variance)}
                            </td>
                            <td className="px-4 py-3 text-right text-slate-300">
                              {row.variance_pct == null ? "—" : pct(row.variance_pct)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </TableWrap>
                ) : (
                  <EmptyState text="No staffing cost-centre variance rows available." />
                )}
              </CardContent>
            </Card>

            <Card className="border-slate-800 bg-slate-950/70 text-slate-100">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Users className="h-5 w-5 text-cyan-400" />
                  Staffing Forecast + Actual Totals
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <MetricCard
                    title="Forecast Staffing"
                    value={money(summary?.forecast_staffing_total)}
                    subtitle="Planned total staffing"
                    valueClassName="text-fuchsia-300"
                    icon={Users}
                  />
                  <MetricCard
                    title="Actual Staffing"
                    value={money(summary?.actual_staffing_total)}
                    subtitle="Captured actual staffing"
                    valueClassName="text-cyan-300"
                    icon={Users}
                  />
                  <MetricCard
                    title="Forecast Staffing Source"
                    value={safeStr(forecast?.staffing_cost_source)}
                    subtitle="Active staffing forecast source"
                    icon={Briefcase}
                  />
                  <MetricCard
                    title="Available Cost Centres"
                    value={
                      Array.isArray(reportPack?.available_cost_centres)
                        ? reportPack.available_cost_centres.length
                        : 0
                    }
                    subtitle="Scoped cost centres in report"
                    icon={ClipboardList}
                  />
                </div>
              </CardContent>
            </Card>
          </>
        ) : null}

        {canViewEventMenu ? (
          <section className="grid grid-cols-1 gap-5 xl:grid-cols-2">
            <Card className="border-slate-800 bg-slate-950/70 text-slate-100">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <UtensilsCrossed className="h-5 w-5 text-cyan-400" />
                  Menu Margin Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                {menuItems.length ? (
                  <TableWrap>
                    <table className="min-w-full text-sm">
                      <thead className="border-b border-slate-800 bg-slate-900/90 text-slate-300">
                        <tr>
                          <th className="px-4 py-3 text-left">Menu Item</th>
                          <th className="px-4 py-3 text-right">Qty</th>
                          <th className="px-4 py-3 text-right">Revenue</th>
                          <th className="px-4 py-3 text-right">Cost</th>
                          <th className="px-4 py-3 text-right">GP</th>
                          <th className="px-4 py-3 text-right">GP %</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800">
                        {menuItems.map((item, index) => (
                          <tr key={`${item.menu_item_id || item.name}-${index}`} className="bg-slate-950/30">
                            <td className="px-4 py-3 text-slate-200">{safeStr(item.name)}</td>
                            <td className="px-4 py-3 text-right text-slate-300">{safeNum(item.qty, 0)}</td>
                            <td className="px-4 py-3 text-right text-slate-300">{money(item.line_total)}</td>
                            <td className="px-4 py-3 text-right text-slate-300">{money(item.line_cost)}</td>
                            <td className="px-4 py-3 text-right text-slate-300">{money(item.line_gp)}</td>
                            <td className="px-4 py-3 text-right text-slate-300">{pct(item.line_gp_pct)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </TableWrap>
                ) : (
                  <EmptyState text="No menu margin lines available." />
                )}
              </CardContent>
            </Card>

            <Card className="border-slate-800 bg-slate-950/70 text-slate-100">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <ClipboardList className="h-5 w-5 text-fuchsia-400" />
                  Benchmark Signals
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <DataPill label="Revenue / Guest" value={money(analysis?.benchmark_signals?.revenue_per_guest)} />
                  <DataPill label="Staffing / Guest" value={money(analysis?.benchmark_signals?.staffing_per_guest)} />
                  <DataPill label="COGS / Guest" value={money(analysis?.benchmark_signals?.cogs_per_guest)} />
                  <DataPill
                    label="Gross Profit / Guest"
                    value={money(analysis?.benchmark_signals?.gross_profit_per_guest)}
                  />
                  <DataPill
                    label="Revenue / Portion"
                    value={money(analysis?.benchmark_signals?.revenue_per_portion)}
                  />
                  <DataPill label="COGS / Portion" value={money(analysis?.benchmark_signals?.cogs_per_portion)} />
                </div>
              </CardContent>
            </Card>
          </section>
        ) : null}

        {canViewEventBilling ? (
          <Card className="border-slate-800 bg-slate-950/70 text-slate-100">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Wallet className="h-5 w-5 text-emerald-400" />
                Commercial Transactions
              </CardTitle>
            </CardHeader>
            <CardContent>
              {commercialTx.length ? (
                <TableWrap>
                  <table className="min-w-full text-sm">
                    <thead className="border-b border-slate-800 bg-slate-900/90 text-slate-300">
                      <tr>
                        <th className="px-4 py-3 text-left">When</th>
                        <th className="px-4 py-3 text-left">Type</th>
                        <th className="px-4 py-3 text-left">Direction</th>
                        <th className="px-4 py-3 text-right">Amount</th>
                        <th className="px-4 py-3 text-left">Method</th>
                        <th className="px-4 py-3 text-left">Reference</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                      {commercialTx.map((tx, index) => (
                        <tr key={`${tx.id || index}`} className="bg-slate-950/30">
                          <td className="px-4 py-3 text-slate-300">{formatDateTime(tx.created_at)}</td>
                          <td className="px-4 py-3 text-slate-200">{safeStr(tx.tx_type)}</td>
                          <td className="px-4 py-3 text-slate-300">{safeStr(tx.direction)}</td>
                          <td className="px-4 py-3 text-right text-slate-300">{money(tx.amount)}</td>
                          <td className="px-4 py-3 text-slate-300">{safeStr(tx.method)}</td>
                          <td className="px-4 py-3 text-slate-300">{safeStr(tx.reference)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableWrap>
              ) : (
                <EmptyState text="No commercial transactions recorded." />
              )}
            </CardContent>
          </Card>
        ) : null}
      </div>
    </div>
  );
}